import { Box, Button, ButtonGroup, Popover, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import {
  colorAccent,
  colorBackground,
  colorBorder,
  colorSurface,
  colorTextMuted,
  colorTextPrimary,
  radiusCard,
} from "src/design/tokens";
import { DateRangePreset } from "src/data/admin/types/analytics";

interface Props {
  preset: DateRangePreset;
  customStart: Dayjs;
  customEnd: Dayjs;
  onPresetChange: (preset: DateRangePreset) => void;
  onCustomRangeChange: (start: Dayjs, end: Dayjs) => void;
}

export const AnalyticsHeader = ({
  preset,
  customStart,
  customEnd,
  onPresetChange,
  onCustomRangeChange,
}: Props) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [pendingStart, setPendingStart] = useState(customStart);
  const [pendingEnd, setPendingEnd] = useState(customEnd);

  const presets: { key: DateRangePreset; label: string }[] = [
    { key: "week", label: t("analytics.dateRange.thisWeek") },
    { key: "month", label: t("analytics.dateRange.thisMonth") },
    { key: "year", label: t("analytics.dateRange.thisYear") },
    { key: "custom", label: t("analytics.dateRange.custom") },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: colorTextPrimary, mb: 0.5 }}>
        {t("analytics.pageTitle")}
      </Typography>
      <Typography sx={{ fontSize: "0.875rem", color: colorTextMuted, mb: 2 }}>
        {t("analytics.pageSubtitle")}
      </Typography>
      <ButtonGroup size="small">
        {presets.map(({ key, label }) => (
          <Button
            key={key}
            onClick={(e) => {
              if (key === "custom") {
                setPendingStart(customStart);
                setPendingEnd(customEnd);
                setAnchorEl(e.currentTarget);
              } else {
                onPresetChange(key);
              }
            }}
            sx={{
              backgroundColor: preset === key ? colorAccent : colorSurface,
              color: preset === key ? "#fff" : colorTextMuted,
              borderColor: `${colorBorder} !important`,
              "&:hover": {
                backgroundColor: preset === key ? colorAccent : colorBackground,
              },
              fontWeight: preset === key ? 600 : 400,
              textTransform: "none",
              px: 2,
            }}
          >
            {label}
          </Button>
        ))}
      </ButtonGroup>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            backgroundColor: colorSurface,
            borderRadius: radiusCard,
            minWidth: 240,
          }}
        >
          <TextField
            label={t("bookings.filters.from")}
            type="date"
            size="small"
            value={pendingStart.format("YYYY-MM-DD")}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setPendingStart(dayjs(e.target.value))}
          />
          <TextField
            label={t("bookings.filters.to")}
            type="date"
            size="small"
            value={pendingEnd.format("YYYY-MM-DD")}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setPendingEnd(dayjs(e.target.value))}
          />
          <Button
            variant="contained"
            size="small"
            sx={{
              backgroundColor: colorAccent,
              "&:hover": { backgroundColor: colorAccent },
              textTransform: "none",
            }}
            onClick={() => {
              onCustomRangeChange(pendingStart, pendingEnd);
              onPresetChange("custom");
              setAnchorEl(null);
            }}
          >
            {t("common.save")}
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};
